import axios from 'axios';
import { APP_MODULE } from 'constants/module.constant';

const axiosInstance = axios.create({
  baseURL: import.meta.env.VITE_REACT_APP_API_BASE_URL,
});

/**
 * Lazy keycloak accessor — keycloak.js imports this file, so we cannot
 * import it back at module load. We resolve the instance from the global
 * registration set in keycloak.js whenever the interceptor runs.
 */
const getKeycloak = () => (typeof window !== 'undefined' ? window.__henddu_keycloak__ : null);

// Single in-flight refresh promise so concurrent requests don't stampede
// keycloak.updateToken() with parallel refreshes.
let refreshInFlight = null;

const ensureFreshToken = async () => {
  const kc = getKeycloak();
  if (!kc || !kc.authenticated) {
    return null;
  }

  if (!refreshInFlight) {
    refreshInFlight = kc
      .updateToken(30) // refresh if token expires within 30s
      .catch((err) => {
        console.error('Token refresh failed:', err);
        throw err;
      })
      .finally(() => {
        refreshInFlight = null;
      });
  }

  await refreshInFlight;
  return kc.token;
};

const ADMIN_TENANT_STORAGE_KEY = 'henddu_admin_selected_tenant';

const getAdminSelectedTenantId = () => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(ADMIN_TENANT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.id ?? null;
  } catch {
    return null;
  }
};

const isPublicWebsiteRoute = () =>
  typeof window !== 'undefined' && window.location.pathname.startsWith('/website');

const applyPublicWebsiteAuthHeaders = (config) => {
  const readOnlyApiKey = import.meta.env.VITE_READ_ONLY_API_KEY;
  if (!readOnlyApiKey) {
    return false;
  }

  config.headers = config.headers ?? {};
  config.headers['X-API-Key'] = readOnlyApiKey;

  if (import.meta.env.VITE_PUBLIC_TENANT_ID) {
    config.headers['X-Public-Tenant-ID'] = import.meta.env.VITE_PUBLIC_TENANT_ID;
  }

  return true;
};

axiosInstance.interceptors.request.use(async (config) => {
  // Skip auth header for opt-out requests (e.g. public endpoints)
  if (config.skipAuth) {
    return config;
  }

  // Public website uses read-only API key instead of Keycloak (matches api.js)
  if (isPublicWebsiteRoute() && applyPublicWebsiteAuthHeaders(config)) {
    return config;
  }

  try {
    const token = await ensureFreshToken();
    if (token) {
      config.headers = config.headers ?? {};
      config.headers.Authorization = `Bearer ${token}`;
    }
  } catch {
    // Refresh failed — let the request proceed with the stale token (if any)
    // and rely on the response interceptor to handle the 401.
    const kc = getKeycloak();
    if (kc?.token) {
      config.headers = config.headers ?? {};
      config.headers.Authorization = `Bearer ${kc.token}`;
    }
  }

  // Inject admin tenant context header when an administrator has selected a tenant
  const adminTenantId = getAdminSelectedTenantId();
  if (adminTenantId) {
    config.headers = config.headers ?? {};
    config.headers['X-Admin-Tenant-ID'] = adminTenantId;
  }

  // Tell the backend which module the SPA is running so it can enforce
  // module-specific role access (e.g. monitoring/industrial/manager).
  if (APP_MODULE) {
    config.headers = config.headers ?? {};
    config.headers['X-App-Module'] = APP_MODULE.toLowerCase();
  }

  return config;
});

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;

    if (status === 401 && originalRequest && !originalRequest._retried) {
      originalRequest._retried = true;
      const kc = getKeycloak();

      if (kc?.authenticated) {
        try {
          // Force a refresh, then retry the original request once.
          await kc.updateToken(-1);
          originalRequest.headers = originalRequest.headers ?? {};
          originalRequest.headers.Authorization = `Bearer ${kc.token}`;
          return axiosInstance(originalRequest);
        } catch {
          // Refresh ultimately failed — log the user out.
          try {
            kc.logout();
          } catch {
            // ignore — we're already in an error path
          }
        }
      }
    }

    return Promise.reject((error.response && error.response.data) || error);
  }
);

export default axiosInstance;

